'use client'

import { useQueryResults } from "@/hooks/useQueryResults";
import { useMemo } from "react";
import { BarChart, ChartSeries } from "./charts/bar-chart";
import moment from "moment";
import { Card, CardContent, CardHeader, CardTitle } from "./ui/card";

export function GrossCashflow() {
    const { results } = useQueryResults({id: 6314629})

    const formatter = new Intl.NumberFormat("en", {
        style: 'currency',
        currency: "USD",
        currencySign: 'standard',
        currencyDisplay: 'symbol',
        maximumFractionDigits: 0
    })

    const formattedData: ChartSeries[] = useMemo(() => {
        if (!results) return []
        const series = [...new Set(results.map(r => r.category))]
        return series.map(group => ({
            id: group as string,
            data: results.filter(r => r.category === group).map(r => ({
                x: r.date.format('YYYY-MM-DD'),
                y: r.total_paid as number
            }))
        }))
    }, [results])

    const cashflowPerYear = useMemo(() => {
        let years = [...new Set(results.map(r => r.date.get('year')))].sort()
        return years.map(yr => ({
            year: yr,
            gcf: results.filter(r => r.date.get('year') === yr).reduce((acc, r) => acc + (r.total_paid as number), 0)
        }))
    }, [results])


    return <div className="flex flex-col w-full gap-4">
        <div className="flex flex-row">
            <span className="block text-2xl lg:ml-8 ml-4 grow">Gross Cashflow</span>
            <span className="block text-xs place-self-end">On-Chain transfers into the Capital Buffer (note: NOT a representation of Revenue)</span>
        </div>
        <div className='grid grid-cols-2 lg:grid-cols-4 text-center gap-4'>
            {cashflowPerYear.map((gcf, i) => <Card key={gcf.year}>
                <CardHeader className='border-b'>
                    <CardTitle className={(i == cashflowPerYear.length - 1)?`animate-breathe`:''}>GCF {gcf.year}</CardTitle>
                </CardHeader>
                <CardContent className='p-4'>
                    {formatter.format(gcf.gcf)}
                </CardContent>
            </Card>)}
        </div>
        <div className="min-h-[30vh] h-[30vh]">
            <BarChart
                data={formattedData}
                stack
                tooltip={{
                    xAxisFormatter: (v) => moment.utc(v).format("MMM YYYY"),
                    seriesValueFormatter: formatter.format
                }}
                showTotal
                height="min-h-[40vh]"
            />
        </div>
    </div>
}

export function GrossCashflowOut() {
    const { results } = useQueryResults({id: 6323958})

    const formatter = new Intl.NumberFormat("en", {
        maximumFractionDigits: 0,
        style: 'currency',
        currency: "USD",
        currencySign: 'standard',
        currencyDisplay: 'symbol'
    })

    const formattedData: ChartSeries[] = useMemo(() => {
        if (!results) return []
        const series = [...new Set(results.map(r => r.name))]
        return series.map(group => ({
            id: group as string,
            data: results.filter(r => r.name === group).map(r => ({
                x: r.date.format('YYYY-MM-DD'),
                y: r.amount as number
            }))
        }))
    }, [results])


    return <div className="w-full h-[40vh]">
        <BarChart
            title="Sky: Cash Out (Savings, OpEx, Capital and Buybacks)"
            description="On-Chain transfers out of the Capital Buffer (including Capital items and Buybacks)"
            data={formattedData}
            stack
            tooltip={{
                xAxisFormatter: (v) => moment.utc(v).format("MMM YYYY"),
                seriesValueFormatter: formatter.format
            }}
            showTotal
        />
    </div>
}